import { defineStore } from "pinia";
import { FabricCanvasData, Layer } from "@xmiles/holopix-types";
import { ref, computed, shallowRef, markRaw, toRaw } from "vue";
import { isPlaceholderObject } from "../../../helper/canvasConfig";

const defaultViewport = {
  zoom: 15,
  pan: { x: 0, y: 0 },
};

/**
 * 图层操作状态管理以及视口信息管理
 */
export const useBoardLayerStore = defineStore("infinite-canvas-layer", () => {
  // #region State - 核心数据
  /**
   * 视口配置
   * 包含：缩放比例、平移位置、中心点等
   */
  const viewport = ref({ ...defaultViewport });

  /** 当前视口缩放比例 */
  const zoom = computed({
    get: () => viewport.value.zoom,
    set: (val) => {
      viewport.value.zoom = val;
    }
  });

  /**
   * Fabric 画布数据快照
   * 包含所有 canvas objects，用于同步 UI 列表展示
   * 使用 shallowRef 避免深度代理，提升大数据量下的性能
   */
  const fabricCanvasData = shallowRef<FabricCanvasData | null>(null);

  /** 当前选中的图层 ID 列表 */
  const selectedLayerCids = ref<string[]>([]);

  /**
   * 上一次选中的主图层 ID
   * 用于处理 shift 多选时的锚点逻辑
   */
  const previousPrimarySelectedLayerCid = ref<string | null>(null);

  /**
   * 剪贴板中的图层数据（完整对象）
   * 使用 shallowRef 避免深度代理
   */
  const copiedLayersData = shallowRef<Layer[]>([]);
  /** 最近一次复制的唯一 id（与系统剪贴板标记配对，用于跨标签页粘贴时判定数据归属） */
  const copiedCopyId = shallowRef<string | null>(null);

  /**
   * 正在播放的视频图层 ID，同一时间只有一个视频可以播放
   */
  const playingVideoLayerId = ref<string | null>(null);

  /**
   * 正在预览的视频图层 ID，用于控制视频预览弹窗
   */
  const videoPreviewLayerId = ref<string | null>(null);

  /**
   * 正在预览的 3D 模型图层 ID，用于控制模型查看弹窗
   */
  const model3dPreviewLayerId = ref<string | null>(null);
  // #endregion

  // #region Getters - 图层计算
  /**
   * 原始图层列表（对应 Fabric 渲染顺序，底部 -> 顶部）
   */
  const layers = computed(() => {
    return fabricCanvasData.value?.objects || [];
  });

  /**
   * UI 展示用的图层列表
   * 1. 过滤掉系统内部图层（workspace, temporary）
   * 2. 反转顺序（顶部 -> 底部），符合 UI 图层列表习惯
   */
  const sortedLayers = computed<Layer[]>(() => {
    const _layers = layers.value || [];

    // 过滤并反转，避免修改原数组
    return _layers
      .filter((item) => {
        // 过滤掉工作区背景图层
        if (item.id === "workspace") return false;
        // 过滤掉临时辅助图层（如选区框、拖拽占位等）
        if ((item as any).temporaryLayerInfo) return false;
        return true;
      })
      .reverse();
  });

  /**
   * 需要渲染的图层列表
   * 1. 过滤内部图层
   * 2. 过滤不可见图层
   * 3. 保持渲染顺序（底部 -> 顶部）
   */
  const renderLayers = computed<Layer[]>(() => {
    const _layers = layers.value || [];
    return _layers.filter((item) => {
      if (item.id === "workspace") return false;
      if ((item as any).temporaryLayerInfo) return false;
      if (!item.visible) return false;
      return true;
    });
  });

  /**
   * 仅包含可见的 UI 图层列表（顶部 -> 底部）
   */
  const visibleLayers = computed<Layer[]>(() => {
    const sorted = sortedLayers.value;
    return sorted.filter((layer) => !!layer.visible);
  });

  /**
   * 当前选中的图层对象列表
   */
  const selectedLayers = computed<Layer[]>(() => {
    // 优化：如果选中数量较多，可以考虑用 Map 优化查找，但通常图层数有限，find 足够
    return selectedLayerCids.value
      .map((cid) => {
        return sortedLayers.value.find((item) => item.id === cid);
      })
      .filter(Boolean) as Layer[];
  });

  /**
   * 可保存的图层列表（目前逻辑同 sortedLayers）
   */
  const savableLayers = computed<Layer[]>(() => {
    return sortedLayers.value;
  });
  // #endregion

  // #region Actions - 数据操作
  /**
   * 更新画布数据快照
   * @param data Fabric 导出的 JSON 数据或兼容的快照数据
   */
  function setFabricCanvasData(data: FabricCanvasData | Record<string, any>) {
    // 使用 markRaw 避免 Vue 深度代理大对象
    fabricCanvasData.value = markRaw(data as FabricCanvasData);

    // 如果数据中包含视口信息，同步更新到 Board Store
    if ((data as any).viewport) {
      updateViewportConfig((data as any).viewport);
    }
  }

  /**
   * 生成当前数据的 JSON 快照
   * 【历史记录规则】快照中不包含占位符和临时图层
   * - 占位符变成结果图后，历史记录撤销重做都不允许有占位符的存在
   * - 临时图层（如扩图预览框、高亮标记）不应进入历史记录
   */
  function generateSnapshot() {
    const rawData = toRaw(fabricCanvasData.value);
    if (!rawData || !rawData.objects) {
      return JSON.stringify(rawData);
    }

    const filteredObjects = rawData.objects.filter(
      (obj: any) => !isPlaceholderObject(obj) && !obj.temporaryLayerInfo
    );

    const cleanedData = {
      ...rawData,
      objects: filteredObjects
    };

    return JSON.stringify(cleanedData);
  }

  /**
   * 重置 store 状态
   */
  function reset() {
    fabricCanvasData.value = null;
    selectedLayerCids.value = [];
    previousPrimarySelectedLayerCid.value = null;
    copiedLayersData.value = [];
    copiedCopyId.value = null;
    playingVideoLayerId.value = null;
    videoPreviewLayerId.value = null;
    model3dPreviewLayerId.value = null;
    // 重置视口为默认值，避免新建画布时继承上一个画布的缩放率
    viewport.value = { ...defaultViewport };
  }

  /**
   * 设置选中图层
   * @param cids 图层 ID 列表
   */
  function setSelectedLayerCids(cids: string[]) {
    selectedLayerCids.value = cids;

    // 更新主选中项（通常是最后一个被点击的）
    if (cids.length > 0) {
      previousPrimarySelectedLayerCid.value = cids[cids.length - 1];
    }
  }

  /**
   * 清空选中状态
   */
  function clearSelectedLayers() {
    selectedLayerCids.value = [];
  }

  /**
   * 播放视频图层
   */
  function setPlayingVideoLayerId(id: string | null) {
    playingVideoLayerId.value = id;
  }

  /**
   * 设置视频预览弹窗的图层 ID
   */
  function setVideoPreviewLayerId(id: string | null) {
    videoPreviewLayerId.value = id;
  }

  /**
   * 设置 3D 模型查看弹窗的图层 ID
   */
  function setModel3dPreviewLayerId(id: string | null) {
    model3dPreviewLayerId.value = id;
  }

  /**
   * 获取工作区背景图层对象
   */
  function getWorkspaceLayer(): Layer | undefined {
    return layers.value?.find((item) => item.id === "workspace");
  }

  /**
   * 设置复制的图层数据
   * @param layers 图层数据列表
   */
  function setCopiedLayersData(layers: Layer[], copyId?: string) {
    copiedLayersData.value = markRaw(layers);
    // 记录本次复制 id，粘贴时与剪贴板标记比对：匹配走内存快路径，不匹配读跨标签页信封
    copiedCopyId.value = copyId ?? null;
  }

  /**
   * 加载快照
   * @param data 快照数据
   */
  function loadSnapshot(data: FabricCanvasData) {
    setFabricCanvasData(data);
  }

  /**
   * 更新视口配置（缩放、位移等）
   * @param newViewport 视口配置片段
   */
  function updateViewportConfig(newViewport: Partial<typeof defaultViewport>) {
    viewport.value = {
      ...viewport.value,
      ...newViewport
    };
  }

  // #endregion

  return {
    fabricCanvasData,
    selectedLayerCids,
    previousPrimarySelectedLayerCid,
    copiedLayersData,
    copiedCopyId,

    layers,
    sortedLayers,
    renderLayers,
    visibleLayers,
    selectedLayers,
    savableLayers,
    playingVideoLayerId,
    videoPreviewLayerId,
    model3dPreviewLayerId,

    viewport,
    zoom,

    setFabricCanvasData,
    generateSnapshot,
    reset,
    setSelectedLayerCids,
    clearSelectedLayers,
    setPlayingVideoLayerId,
    setVideoPreviewLayerId,
    setModel3dPreviewLayerId,
    getWorkspaceLayer,
    setCopiedLayersData,
    loadSnapshot,

    updateViewportConfig,
  };
});
